import React from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, borderRadius, spacing } from '../../theme/tokens';
import Text, { TextAlign, TextSize } from './text';

export enum TagVariant {
  DEFAULT = 'default',
  EXPERT = 'expert',
  OUTLINED = 'outlined'
}

export enum TagSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

interface TagProps {
  variant?: TagVariant;
  size?: TagSize;
  children: React.ReactNode;
  className?: string;
}

interface TextContainerProps {
  tags?: string[];
  text?: string;
  variant?: TagVariant;
  size?: TagSize;
  className?: string;
}

const tagSizeStyles = {
  [TagSize.SM]: css`
    padding: 2px 6px;
    font-size: 0.75em;
  `,
  [TagSize.MD]: css`
    padding: 3px 8px;
    font-size: 0.85em;
  `,
  [TagSize.LG]: css`
    padding: 4px 12px;
    font-size: 0.9em;
  `
};

const tagVariantStyles = {
  [TagVariant.DEFAULT]: css`
    background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
    color: ${colors.text.primary};
    border: 1px solid transparent;
  `,
  [TagVariant.EXPERT]: css`
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%);
    color: #192947;
    border: 1px solid #dce5f4;
  `,
  [TagVariant.OUTLINED]: css`
    background-color: transparent;
    color: ${colors.text.primary};
    border: 1px solid ${colors.border.default};
  `
};

const StyledTag = styled.span<{ variant: TagVariant; size: TagSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};
  white-space: nowrap;
  transition: all 0.2s ease;
  
  ${({ variant }) => tagVariantStyles[variant]}
  ${({ size }) => tagSizeStyles[size]}
  
  &:hover {
    opacity: 0.8;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 0.875em;
  color: #455776;
  line-height: 1.4;
`;

const SingleTagContainer = styled.div`
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  border-radius: 6px;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Tag: React.FC<TagProps> = ({
  variant = TagVariant.DEFAULT,
  size = TagSize.MD,
  children,
  className
}) => (
  <StyledTag variant={variant} size={size} className={className}>
    {children}
  </StyledTag>
);

const TextContainer: React.FC<TextContainerProps> = ({ 
  tags, 
  text, 
  variant = TagVariant.DEFAULT, 
  size = TagSize.MD,
  className 
}) => {
  if (tags && tags.length > 0) {
    return (
      <StyledContainer className={className}>
        {tags.map((tag, index) => (
          <Tag key={index} variant={variant} size={size}>
            {tag}
          </Tag>
        ))}
      </StyledContainer>
    );
  }

  if (text) {
    return (
      <SingleTagContainer className={className}>
        <Text size={TextSize.XS} align={TextAlign.CENTER}>
          {text}
        </Text>
      </SingleTagContainer>
    );
  }

  return null;
};

export default TextContainer; 